import type { ComponentType, ReactElement } from "react";

import type { FiltersListProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/components/FiltersList/types";
import { filterTrackingRegistry } from "app/pages/sales-intelligence/modules/find-apps-filters/services/tracking/formatters";
import findAppsTrackingService from "app/pages/sales-intelligence/modules/find-apps-filters/services/tracking/trackingService";
import type { AppsSourceType } from "app/pages/sales-intelligence/store/find-apps/types/appsSourceType";
import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

type Props = { trackingTab: AppsSourceType };

type ExpectedHOCProps<T extends IPlainObject = IPlainObject> = Omit<FiltersListProps<T>, "onTrackFilterUpdate"> & Props;

function withFindAppsTracking<T extends IPlainObject = IPlainObject>(
    ConsumerComponent: ComponentType<FiltersListProps<T>>,
): ComponentType<ExpectedHOCProps<T>> {
    return function WrappedWithFindAppsTracking({
        onToggleExpand,
        onFilterUpdate,
        trackingTab,
        expandedFilter,
        ...props
    }: ExpectedHOCProps<T>): ReactElement {
        const handleFilterUpdate = (key: keyof T, value?: T[keyof T]) => {
            const filterKey = String(key);
            const valueForTracking = filterTrackingRegistry.format(filterKey, value);

            findAppsTrackingService.trackFilterValue(trackingTab, filterKey, valueForTracking);
            onFilterUpdate(key, value);
        };

        const handleToggleExpand = (filterName: keyof T) => {
            findAppsTrackingService.trackExpandCollapseFilter(
                String(filterName) !== expandedFilter,
                trackingTab,
                String(filterName),
            );

            onToggleExpand(filterName);
        };

        return (
            <ConsumerComponent
                {...props}
                expandedFilter={expandedFilter}
                onToggleExpand={handleToggleExpand}
                onFilterUpdate={handleFilterUpdate}
            />
        );
    };
}

export default withFindAppsTracking;
